"use client";

// ── The newest announcement, on whatever contest page the contestant is on ──
// `EX-ANN01`. The alert sits above the workspace (and above the contest
// page's panels) until the contestant acknowledges it. It shows the newest
// announcement this browser has not acknowledged, says how many more there
// are, and links to the full list. The tab title's count is the page's own
// (`useTitleCount`), since a page can show this and the clarification alert.
// Nothing here is fetched: `useAnnouncementInbox` owns the poll and the marker.

import { useTranslations } from "next-intl";

import type { ContestAnnouncement } from "../types";
import { ContestNoticeAlert } from "./ContestNoticeAlert";

export function ContestAnnouncementAlert({
  unseen,
  onSeen,
  readAllHref,
  onReadAll,
}: {
  /** Newest first, from `useAnnouncementInbox`. */
  unseen: readonly ContestAnnouncement[];
  onSeen: () => void;
  readAllHref?: string;
  onReadAll?: () => void;
}) {
  const t = useTranslations("contest.announcements");
  const newest = unseen[0];
  if (!newest) return null;
  const more = unseen.length - 1;
  return (
    <ContestNoticeAlert
      testId="contest-announcement-alert"
      label={`${t("alertLabel")}${newest.editedAt ? ` · ${t("alertEdited")}` : ""}`}
      heading={newest.title}
      body={newest.body}
      more={more > 0 ? t("alertMore", { count: more }) : null}
      readAllLabel={t("alertReadAll")}
      readAllHref={readAllHref}
      onReadAll={onReadAll}
      gotItLabel={t("alertGotIt")}
      onSeen={onSeen}
    />
  );
}
